import { type FormEvent, useState } from "react";
import { api } from "../api/client.ts";
import { useAuth } from "./AuthProvider.tsx";

function Wordmark() {
  return (
    <div className="brand-row">
      <div className="brand-mark">S</div>
      <div className="brand-name">
        Send<span>.</span>
      </div>
    </div>
  );
}

export function SignIn() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "sign in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit}>
        <Wordmark />
        <h1>Sign in</h1>
        <p className="sub">Internal sending platform.</p>

        {error && <div className="error">{error}</div>}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

/**
 * Shown only while no user exists. Once the first admin is created the API
 * closes this route permanently, so there is no lingering way in.
 */
export function Bootstrap() {
  const { refresh, signIn } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/auth/bootstrap", { name, email, password });
      await signIn(email, password);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "could not create the account");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={submit}>
        <Wordmark />
        <h1>Create the first admin</h1>
        <p className="sub">
          Nobody has an account yet. This account will be able to configure Postal, domains and
          senders.
        </p>

        {error && <div className="error">{error}</div>}

        <div className="field">
          <label htmlFor="name">Name</label>
          <input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="bs-email">Email</label>
          <input
            id="bs-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="bs-password">Password</label>
          <input
            id="bs-password"
            type="password"
            autoComplete="new-password"
            minLength={12}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div className="hint">At least 12 characters. Length matters more than symbols.</div>
        </div>

        <button className="btn" type="submit" disabled={busy}>
          {busy ? "Creating…" : "Create admin account"}
        </button>
      </form>
    </div>
  );
}
